import { agentCounts, agentsApi, byUrgency, pollWhile } from '../../agents-api.ts';
import type { AgentInfo, ProjectInfo } from '../../agents-api.ts';
import { button, emptyState, pageHeader } from '../../components.ts';
import { icon } from '../../icons.ts';
import { h } from '../../ui.ts';
import { newAgentDialog } from './new-agent.ts';
import { agentRow, isEditing } from './row.ts';

/** Agents, grouped by project, what needs you first (design-docs/0030). Polled every 3 s while open. */
export async function agentsView(): Promise<HTMLElement> {
  const [agents, projects] = await Promise.all([agentsApi.list(), agentsApi.projects().catch(() => [])]);
  const sub = h('span', {}, summary(agents));
  const list = h('div', { class: 'agent-groups' });
  const own = projects.filter((p) => !p.linearOrg);
  const root = h(
    'div',
    { class: 'page' },
    pageHeader(
      'Agents',
      sub,
      own.length ? button([icon.plus(), 'New agent'], () => newAgentDialog(own), { extra: 'glow' }) : null,
    ),
    list,
  );
  const draw = (all: AgentInfo[]) => {
    sub.textContent = summary(all);
    renderGroups(list, all, projects, () => void refresh());
  };
  const refresh = async () => draw(await agentsApi.list());
  draw(agents);
  pollWhile(root, 3000, refresh);
  return root;
}

function summary(agents: AgentInfo[]): string {
  if (!agents.length) return 'Coding agents she runs on your projects, each on its own branch.';
  const c = agentCounts(agents);
  const parts = [`${c.running} running`, `${c.asking} need${c.asking === 1 ? 's' : ''} you`];
  if (c.previews) parts.push(`${c.previews} preview${c.previews === 1 ? '' : 's'} ready`);
  return `${parts.join(' · ')} · ${agents.length} in total`;
}

/** Groups by project, the group with the most urgent agent first; rows are kept while someone types in them. */
export function renderGroups(host: HTMLElement, agents: AgentInfo[], projects: ProjectInfo[], changed: () => void) {
  if (!agents.length) {
    host.replaceChildren(
      emptyState(
        'No agents yet',
        'Start one from a project, or ask her in a talk: “build a pricing page for Convasi”.',
      ),
    );
    return;
  }
  host.querySelector(':scope > .empty')?.remove();
  const groups = new Map<string, AgentInfo[]>();
  for (const a of [...agents].sort(byUrgency)) groups.set(a.project, [...(groups.get(a.project) ?? []), a]);
  const els = [...groups].map(([slug, list]) => group(host, slug, list, projects, changed));
  if (!sameOrder(host, els)) host.replaceChildren(...els);
}

function group(host: HTMLElement, slug: string, agents: AgentInfo[], projects: ProjectInfo[], changed: () => void) {
  let el = host.querySelector<HTMLElement>(`:scope > [data-project="${CSS.escape(slug)}"]`);
  if (!el) {
    const name = projects.find((p) => p.slug === slug)?.name ?? slug;
    el = h(
      'section',
      { class: 'agent-group', 'data-project': slug, 'aria-label': name },
      h(
        'a',
        { class: 'agent-group-head', href: `#/projects/${encodeURIComponent(slug)}` },
        icon.folder(14),
        h('span', {}, name),
        h('span', { class: 'count mono' }),
      ),
      h('div', { class: 'agent-rows' }),
    );
  }
  el.querySelector('.count')!.textContent = String(agents.length);
  const rows = el.querySelector<HTMLElement>('.agent-rows')!;
  const next = agents.map((a) => {
    const old = rows.querySelector<HTMLElement>(`:scope > [data-id="${CSS.escape(a.id)}"]`);
    if (old && (old.dataset.v === String(a.updatedAt) || isEditing(old))) return old;
    const row = agentRow(a, { project: false, onChange: changed });
    old?.replaceWith(row);
    return row;
  });
  if (!sameOrder(rows, next)) rows.replaceChildren(...next);
  return el;
}

const sameOrder = (parent: HTMLElement, want: HTMLElement[]) =>
  parent.children.length === want.length && want.every((el, i) => parent.children[i] === el);
